import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw9339Api, QueryRequest, QueryResponse } from '../../../services/pw9339Api';

export interface PW9339QueryState {
  loading: boolean;
  result: QueryResponse | null;
  queried: boolean;
  noData: boolean;
  defaultYear: number;
  defaultMonth: string;
}

export interface PW9339QueryActions {
  runQuery: (req: QueryRequest) => Promise<void>;
  reset: () => void;
}

export function usePW9339Query(): PW9339QueryState & PW9339QueryActions {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<QueryResponse | null>(null);
  const [queried, setQueried] = useState(false);
  const [noData, setNoData] = useState(false);
  const [defaultYear, setDefaultYear] = useState(new Date().getFullYear());
  const [defaultMonth, setDefaultMonth] = useState(
    String(new Date().getMonth() + 1).padStart(2, '0')
  );

  // Fetch defaults from backend on mount
  useEffect(() => {
    pw9339Api.getDefaults().then((d) => {
      if (d.year > 0) setDefaultYear(d.year);
      if (d.month) setDefaultMonth(d.month);
    }).catch(() => {
      // Use client-side defaults on error
    });
  }, []);

  const runQuery = useCallback(async (req: QueryRequest) => {
    setLoading(true);
    setQueried(false);
    setNoData(false);
    try {
      const resp = await pw9339Api.query(req);
      if (resp.deptGroups && resp.deptGroups.length > 0) {
        setResult(resp);
        setNoData(false);
      } else {
        setResult(null);
        setNoData(true);
        message.info('查無符合條件的薪資資料');
      }
      setQueried(true);
    } catch {
      message.error('查詢失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setQueried(false);
    setNoData(false);
  }, []);

  return { loading, result, queried, noData, defaultYear, defaultMonth, runQuery, reset };
}
